import { portalGet, portalPost } from '@/features/portal/utils/portalRequest';
import { apiGet, apiPost } from '@/shared/utils/requestBuilder';
import { assertUrlNavegavelSegura } from '@/shared/utils/urlSegura';
import { certificadoAcessosDtoSchema } from '@/shared/validation/certificadoAcessosDtoSchemas';
import type { CertificadoGerado } from '../../types/arbitralDetailTypes';
import type {
  CertificadoAcessosArbitral,
  OrigemCertificadoAcessos,
  OrigemPdfCertificadoAcessos,
  SolicitacaoPdfCertificadoAcessos,
} from '../../types/certificadoAcessosTypes';
import { mapCertificadoAcessos } from '../../utils/certificadoAcessosMapper';
import { lerSolicitacaoPdfCertificado } from '../../utils/pdfCertificadoDoServidor';

const TIMEOUT_MS = 10000;

const ERRO_PDF = 'Erro ao gerar o PDF do Certificado de Acessos';

const BUSCAR_POR_ORIGEM: Record<OrigemCertificadoAcessos, (id: string) => Promise<Response>> = {
  parte: (id) =>
    apiGet(
      `/arbitral/processos/${id}/certificado-acessos`,
      undefined,
      false,
      AbortSignal.timeout(TIMEOUT_MS),
    ),
  notificado: (id) =>
    portalGet(
      `/arbitral/portal/citacoes/${id}/certificado-acessos`,
      undefined,
      false,
      undefined,
      TIMEOUT_MS,
    ),
};

/**
 * Cada porta pede o PDF na própria rota, com a própria sessão. A do notificado é a única que devolve
 * outro arquivo: a cópia redigida da parte dele, porque a peça integral leva os dados de todas as
 * partes. O `id` do portal é a parte (citação), não o processo — o mesmo de `BUSCAR_POR_ORIGEM`.
 *
 * O `signal` de quem pediu entra junto com o timeout: sair da tela cancela também o POST em voo, e
 * não só a espera entre as perguntas (review do web#1190, CodeRabbit).
 */
const SOLICITAR_PDF_POR_ORIGEM: Record<
  OrigemPdfCertificadoAcessos,
  (id: string, signal: AbortSignal) => Promise<Response>
> = {
  parte: (id, signal) =>
    apiPost(`/arbitral/processos/${id}/certificado-acessos/gerar-pdf`, undefined, false, signal),
  secretaria: (id, signal) =>
    apiPost(
      `/admin/arbitral/processos/${id}/certificado-acessos/gerar-pdf`,
      undefined,
      false,
      signal,
    ),
  arbitro: (id, signal) =>
    apiPost(
      `/arbitral/painel/processos/${id}/certificado-acessos/gerar-pdf`,
      undefined,
      false,
      signal,
    ),
  notificado: (id, signal) =>
    portalPost(
      `/arbitral/portal/citacoes/${id}/certificado-acessos/gerar-pdf`,
      undefined,
      false,
      undefined,
      signal,
    ),
};

export const certificadosService = {
  getCertificadoAcessos: async (
    processoId: string,
    origem: OrigemCertificadoAcessos = 'parte',
  ): Promise<CertificadoAcessosArbitral> => {
    const response = await BUSCAR_POR_ORIGEM[origem](processoId);
    if (!response.ok) {
      throw new Error('Erro ao carregar o Certificado de Acessos');
    }
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      throw new Error('Erro ao carregar o Certificado de Acessos');
    }
    const dto = certificadoAcessosDtoSchema.parse((body as { data: unknown }).data);
    return mapCertificadoAcessos(dto);
  },

  /**
   * Pede ao servidor o PDF do Certificado de Acessos — o único que o `/autenticar` reconhece,
   * porque é dele que o `hashArquivo` foi calculado na emissão. Rasterizar a tela com
   * `domToCanvas` produz bytes que nunca existiram no servidor: o documento baixado era, por
   * desenho, reprovado pela própria verificação de não-adulteração.
   *
   * O endpoint é idempotente do lado do back (trava + relê a certidão antes de emitir outra),
   * então repergunta durante o `GENERATING` não multiplica peças nos autos.
   */
  solicitarPdfCertificadoAcessos: async (
    processoId: string,
    origem: OrigemPdfCertificadoAcessos,
    signal: AbortSignal,
  ): Promise<SolicitacaoPdfCertificadoAcessos> => {
    const response = await SOLICITAR_PDF_POR_ORIGEM[origem](
      processoId,
      AbortSignal.any([signal, AbortSignal.timeout(TIMEOUT_MS)]),
    );
    return lerSolicitacaoPdfCertificado(response, ERRO_PDF, 'Link do Certificado de Acessos');
  },

  list: async (processoId: string): Promise<CertificadoGerado[]> => {
    const response = await apiGet(
      `/arbitral/processos/${processoId}/certificados`,
      undefined,
      false,
      AbortSignal.timeout(TIMEOUT_MS),
    );

    if (!response.ok) {
      throw new Error('Erro ao listar certificados');
    }

    const body = await response.json();
    return body.data as CertificadoGerado[];
  },

  download: async (processoId: string, certId: string): Promise<string> => {
    const response = await apiGet(
      `/arbitral/processos/${processoId}/certificados/${certId}/download`,
      undefined,
      false,
      AbortSignal.timeout(TIMEOUT_MS),
    );
    if (!response.ok) throw new Error('Erro ao baixar certificado');
    const body = await response.json();
    return assertUrlNavegavelSegura(body?.data?.url, 'Link do certificado');
  },
};
